import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TemplatePreviewModal = ({ template, onClose, onUse, applying, applyError }) => {
  if (!template) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-1 line-clamp-1">
              {template?.name}
            </h2>
            <div className="flex flex-wrap items-center gap-2 md:gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="Tag" size={14} />
                {template?.category}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Star" size={14} className="fill-warning text-warning" />
                {template?.rating}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Download" size={14} />
                {template?.downloads}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={applying}
            className="ml-4 w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div className="aspect-video rounded-lg overflow-hidden border border-border bg-muted">
              <Image
                src={template?.previewImage}
                alt={template?.previewImageAlt}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <h3 className="font-semibold text-base md:text-lg text-foreground mb-3">
                  Description
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {template?.fullDescription}
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-base md:text-lg text-foreground mb-3">
                  Features
                </h3>
                <ul className="space-y-2">
                  {template?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm md:text-base text-muted-foreground">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-base md:text-lg text-foreground mb-3">
                Included Pages
              </h3>
              <div className="flex flex-wrap gap-2">
                {template?.includedPages?.map((page, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-muted rounded-lg text-sm text-foreground"
                  >
                    <Icon name="FileText" size={14} />
                    {page}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-base md:text-lg text-foreground mb-3">
                Technical Specifications
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {template?.specifications?.map((spec, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0" />
                    <span>{spec}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {applyError && (
          <div className="flex items-center gap-2 px-4 md:px-6 py-3 bg-error/10 border-t border-error/20 text-error text-sm">
            <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
            <span>{applyError}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            fullWidth
            iconName="ExternalLink"
            iconPosition="left"
            disabled={applying}
            onClick={() => window.open(template?.demoUrl, '_blank')}
          >
            View Live Demo
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName={applying ? 'Loader2' : 'Plus'}
            iconPosition="left"
            disabled={applying}
            onClick={() => onUse(template)}
          >
            {applying ? 'Creating Page...' : 'Use This Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;